import type { TooltipComponentFormatterCallbackParams } from 'echarts'

import { addDays, differenceInDays, format, isDate } from 'date-fns'
import { isPlainObject } from 'lodash/fp'

import { formatNumber } from '@/shared/tools/locale'

type AnyRecord = Record<string, any>
const commonDateFormat = 'MMM d, yyyy'
export const getDefaultChartData = ({
	start,
	end,
	key = 'count',
}: {
	start: string
	end: string
	key?: string
}) => {
	const diffDays = differenceInDays(end, start)

	return Array.from({ length: diffDays || 1 }, () => ({
		date: '',
		[key]: 0,
	})).map((item, index) => {
		item.date = format(addDays(start, index), commonDateFormat)

		return item
	})
}

function escapeHtml(input: unknown): string {
	const str = String(input ?? '')

	return str
		.replaceAll('&', '&amp;')
		.replaceAll('<', '&lt;')
		.replaceAll('>', '&gt;')
		.replaceAll('"', '&quot;')
		.replaceAll("'", '&#039;')
}

function formatTooltipValue(value: unknown): string {
	if (value == null) return ''

	if (typeof value === 'number') {
		return formatNumber(value)
	}

	if (typeof value === 'string') {
		return value
	}

	// Common ECharts shapes:
	// - scatter: [x, y] or [x, y, size]
	// - candlestick: [open, close, low, high]
	if (Array.isArray(value)) {
		const last = value[value.length - 1]
		if (typeof last === 'number') {
			return formatNumber(last)
		}
		if (last === null) {
			return '-'
		}

		return escapeHtml(value.join(', '))
	}

	if (isPlainObject(value)) {
		const maybe = (value as AnyRecord).value
		if (maybe !== undefined) {
			return formatTooltipValue(maybe)
		}
	}

	return escapeHtml(String(value))
}
function getTooltipColor(param: any): string {
	const c = param?.color
	if (typeof c === 'string') return c

	// Gradients / complex colors (best-effort)
	const stops = (c as any)?.colorStops
	const firstStop = Array.isArray(stops) ? (stops[0] as any)?.color : undefined
	if (typeof firstStop === 'string') return firstStop

	return 'currentColor'
}

function tooltipHeader(params: any): string {
	if (Array.isArray(params) && params.length > 0) {
		const first: any = params[0]
		const val = first?.axisValueLabel ?? first?.name ?? first?.axisValue ?? ''
		if (isDate(new Date(val))) {
			return format(new Date(val), commonDateFormat)
		}

		return val
	}

	return params?.name ?? params?.seriesName ?? ''
}

function tooltipItems(
	params: any,
	formatValue?: (value: number) => string
): { label: string; value: string; color: string }[] {
	const list = Array.isArray(params) ? params : [params]

	return list
		.filter(Boolean)
		.filter((item) => {
			const value = item.value as unknown[] | undefined

			return Array.isArray(value) && value[1] != null
		})
		.map((p) => ({
			label: p?.seriesName ?? p?.name ?? 'Value',
			value:
				formatValue && Array.isArray(p?.value) && typeof p.value[1] === 'number'
					? formatValue(p.value[1])
					: formatTooltipValue(p?.value ?? p?.data?.value ?? p?.data),
			color: getTooltipColor(p),
		}))
}

function tooltipContainerStyle(): string {
	return [
		'min-width: 8rem',
		'border: 1px solid var(--border)',
		'background: var(--popover)',
		'color: var(--popover-foreground)',
		'border-radius: 0.625rem',
		'padding: 0.375rem 0.625rem',
		'box-shadow: 0 10px 15px -3px rgba(0,0,0,0.10), 0 4px 6px -4px rgba(0,0,0,0.10)',
		'font-size: 0.75rem',
		'line-height: 1.2',
	].join(';')
}

export function shadcnHtmlTooltipFormatter(
	params: TooltipComponentFormatterCallbackParams,
	formatValue?: (value: number) => string
): string {
	const header = escapeHtml(tooltipHeader(params))

	const items = tooltipItems(params, formatValue)
	const seen = new Set<string>()
	const headerHtml = header
		? `<div style="font-weight: 500; margin-bottom: 0.375rem;">${header}</div>`
		: ''

	const rows = items
		.filter((item) => {
			const name = item.label ?? ''
			if (seen.has(name)) {
				return false
			}
			seen.add(name)

			return true
		})
		.map((item) => {
			const label = escapeHtml(item.label)
			const value = escapeHtml(item.value)
			const color = escapeHtml(item.color)

			return `
        <div style="display:flex; align-items:center; justify-content:space-between; gap: 0.75rem;">
          <div style="display:flex; align-items:center; gap: 0.5rem; min-width: 0;">
            <span style="width:0.625rem; height:0.625rem; border-radius:0.25rem; background:${color}; flex: 0 0 auto;"></span>
            <span style="color: var(--muted-foreground); overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${label}</span>
          </div>
          <span style="font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace; font-variant-numeric: tabular-nums; font-weight: 500;">
            ${value}
          </span>
        </div>
      `
		})
		.join('')

	return `<div style="${tooltipContainerStyle()}">${headerHtml}<div style="display:grid; gap: 0.375rem;">${rows}</div></div>`
}
